import { useCallback, useMemo } from 'react';
import { useIoContext } from "../../../../../contexts";
import { useServiceBillingDetails } from "../../../../../hooks/reactQuery/queries/useServiceBillingDetails";
import { isAfterOrEqual, format, startOfDay, isBefore } from "../../../../../infra/utils/dates-fns";
import { serviceModel } from "../../../../../models";
import { pluralize } from "../../../../../utils/pluralize";
import { InlineStartBillingMenuItem } from "../InlineStartBillingControl";
export const useCallouts = _ref => {
  let {
    testIds,
    service,
    relationshipEffectiveDate,
    selectedItem,
    isAmendedService
  } = _ref;
  const {
    InlineStartBillingControlTestIds
  } = testIds;
  const {
    getNow
  } = useIoContext();
  const getToday = useCallback(() => startOfDay(getNow()), [getNow]);
  const isProrated = serviceModel.isProrated(service);
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const startBillingAt = serviceModel.startBillingAt(service);
  const {
    nextBillingDate,
    periods
  } = useServiceBillingDetails({
    service,
    relationshipEffectiveDate
  });
  const shouldShowProrateWarning = useMemo(() => {
    if (!isProrated && isBilledUpfront && startBillingAt && nextBillingDate) {
      return nextBillingDate.getTime() !== startBillingAt.getTime();
    }
    return false;
  }, [nextBillingDate, startBillingAt, isProrated, isBilledUpfront]);
  return useMemo(() => {
    const callouts = [];
    if (selectedItem !== InlineStartBillingMenuItem.SpecificDate || !startBillingAt) {
      return callouts;
    }
    if (!isAmendedService && isAfterOrEqual(startBillingAt, getToday())) {
      if (relationshipEffectiveDate) {
        callouts.push({
          dataTestid: InlineStartBillingControlTestIds.Callouts.FutureDate(),
          text: "If the proposal is signed after ".concat(format(startBillingAt, 'MMM d, yyyy'), ", any charges for prior periods will be invoiced upon approval.")
        });
      } else {
        callouts.push({
          dataTestid: InlineStartBillingControlTestIds.Callouts.OnAcceptanceDate(),
          text: "Since the agreement's effective date is set to \"On acceptance,\" signing it after ".concat(format(startBillingAt, 'MMM d, yyyy'), " will start billing in the next cycle and exclude prior charges.")
        });
      }
    }
    if (shouldShowProrateWarning) {
      callouts.push({
        dataTestid: InlineStartBillingControlTestIds.Callouts.Proration(),
        text: "As the selected date doesn't match the billing day, an additional full invoice will be issued. To avoid this, change the date or prorate the first payment."
      });
    }
    if (periods && periods > 0 && isBefore(startBillingAt, getToday())) {
      callouts.push({
        dataTestid: InlineStartBillingControlTestIds.Callouts.PriorPeriods(),
        text: "Invoices for ".concat(periods, " prior ").concat(pluralize('period', periods), " will be issued once the proposal is approved. If the proposal is signed after ").concat(format(startBillingAt, 'MMM d, yyyy'), ", additional invoices for earlier periods will also be issued.")
      });
    }
    return callouts;
  }, [selectedItem, startBillingAt, shouldShowProrateWarning, periods, relationshipEffectiveDate, isAmendedService, getToday, InlineStartBillingControlTestIds]);
};